import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { BlueButton, NavButton } from 'components/Button'
import { MainHeadline, SubDescription } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCard, CampaignBreadCard, Card } from 'components/Card'
import { FormLabel } from 'components/Label'
import { DatetimePicker, ImageUploader, SearchForm, SelectBox, TagCheckbox, TextInput } from 'components/Form'
import { useSelectCallback, useStringChangeEvent } from 'hooks/form'
import { State } from 'index'
import { getConfirmCampaign } from 'reducks/campaign/selectors'
import { getBreadShortName, parseDateToDatetimeLocal } from 'lib/helper'
import { publishStatusOptions } from 'constants/options'
import { confirmCampaign, fetchCampaign } from 'reducks/campaign/operations'
import { FirebaseTimestamp } from 'firebase/index'
import { CampaignStatus } from 'types/campaign'
import { useSearchBreads } from 'hooks/campaign'
import { Background } from 'components/Background'
import { FilterLabel } from 'components/Order'
import { FlexRow } from 'components/Flex'
import { BREAD_TYPES } from 'constants/bread'
import { useBreadTypeFilter } from 'hooks/bread'

type Props = {
  id: string
  isCreatePage: boolean
}

const CampaignEditTemplate: FC<Props> = (props) => {
  const { id, isCreatePage } = props
  const dispatch = useDispatch()
  const confirm = useSelector((state: State) => getConfirmCampaign(state))

  const [name, setName] = useState<string>('')
  const [banner, setBanner] = useState<string>('')
  const [mainVisual, setMainVisual] = useState<string>('')
  const [background, setBackground] = useState<string>('')
  const [periodStart, setPeriodStart] = useState<string>('')
  const [periodEnd, setPeriodEnd] = useState<string>('')
  const [status, setStatus] = useState<string>('private')
  const [breadIds, setBreadIds] = useState<string[]>([])
  const { keyword, handleKeyword, searchedBreads, addedBreads, searchBreads } = useSearchBreads(breadIds)
  const { breadTypes, handleBreadTypes, isAllBreadTypesSelected, selectAllBreadTypes } = useBreadTypeFilter()

  const handleName = useStringChangeEvent(setName)
  const handleBanner = useSelectCallback(setBanner)
  const handleMainVisual = useSelectCallback(setMainVisual)
  const handleBackground = useSelectCallback(setBackground)
  const handlePeriodStart = useStringChangeEvent(setPeriodStart)
  const handlePeriodEnd = useStringChangeEvent(setPeriodEnd)
  const selectStatus = useSelectCallback(setStatus)

  const handleBreadIds = useCallback(
    (breadId: string) => {
      setBreadIds((prevState) => {
        if (prevState.includes(breadId)) {
          return prevState.filter((_breadId) => _breadId !== breadId)
        } else {
          return [...prevState, breadId]
        }
      })
    },
    [setBreadIds]
  )

  const goToConfirmPage = useCallback(() => {
    dispatch(
      confirmCampaign({
        name,
        banner,
        main_visual: mainVisual,
        background,
        period_start: FirebaseTimestamp.fromDate(new Date(periodStart)),
        period_end: FirebaseTimestamp.fromDate(new Date(periodEnd)),
        status: status as CampaignStatus,
        bread_ids: breadIds,
        campaign_id: id,
      })
    )
  }, [name, banner, mainVisual, background, periodStart, periodEnd, status, breadIds, id])

  useEffect(() => {
    if (!confirm) return
    setName(confirm.name)
    setBanner(confirm.banner)
    setMainVisual(confirm.main_visual)
    setBackground(confirm.background)
    setPeriodStart(parseDateToDatetimeLocal(confirm.period_start.toDate()))
    setPeriodEnd(parseDateToDatetimeLocal(confirm.period_end.toDate()))
    setStatus(confirm.status)
    setBreadIds(confirm.bread_ids)
  }, [confirm])

  /**
   * Firestoreのキャンペーン情報を取得する
   * ただし、新規作成→確認画面→編集画面と遷移した場合はFirestoreにデータが存在しないので関数内でundefinedをreturn
   */
  useEffect(() => {
    if (!id) return
    dispatch(fetchCampaign(id))
  }, [id])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={isCreatePage ? 'キャンペーン作成' : 'キャンペーン編集'} />
      <Spacer size={20} />
      <AlertCard
        headline={
          '公開ステータスが「公開」かつ掲載期間内のものは、アプリ側に即時反映されるため登録内容にはご注意ください'
        }
        title={'注意'}
      />
      <Spacer size={16} />
      <FormLabel id={'name'} label={'キャンペーン名'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'アプリの見出しに表示される名前です'} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        required={true}
        onChange={handleName}
        placeholder={'2021クリスマスキャンペーン'}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={16} />
      <FormLabel id={'banner_label'} label={'バナー画像'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'アプリのマップ画面に表示されるバナー画像です。'} />
      <Spacer size={8} />
      <ImageUploader
        folderPath={`campaigns/${id}`}
        handleImageUrl={handleBanner}
        imageName={'banner'}
        imageUrl={banner}
      />
      <Spacer size={16} />
      <FormLabel id={'main_visual_label'} label={'メインビジュアル'} required={true} />
      <Spacer size={4} />
      <SubDescription
        description={
          'キャンペーンのメインビジュアルに表示される画像です。\n' + '見出し文章も含めた画像で登録してください。'
        }
      />
      <Spacer size={8} />
      <ImageUploader
        folderPath={`campaigns/${id}`}
        handleImageUrl={handleMainVisual}
        imageName={'main_visual'}
        imageUrl={mainVisual}
      />
      <Spacer size={16} />
      <FormLabel id={'background_label'} label={'背景リピート'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'キャンペーンページの背景画像を変える場合は登録してください。'} />
      <Spacer size={8} />
      <ImageUploader
        folderPath={`campaigns/${id}`}
        handleImageUrl={handleBackground}
        imageName={'background'}
        imageUrl={background}
      />
      <Spacer size={16} />
      <FormLabel id={'period_start'} label={'掲載開始日'} required={true} />
      <Spacer size={8} />
      <DatetimePicker onChange={handlePeriodStart} value={periodStart} />
      <Spacer size={16} />
      <FormLabel id={'period_end'} label={'掲載終了日'} required={true} />
      <Spacer size={8} />
      <DatetimePicker onChange={handlePeriodEnd} value={periodEnd} />
      <Spacer size={16} />
      <FormLabel id={'period_start'} label={'公開ステータス'} required={true} />
      <Spacer size={8} />
      <SelectBox options={publishStatusOptions} select={selectStatus} value={status} />
      <Spacer size={16} />
      <MainHeadline headline={'紹介する商品'} />
      {addedBreads.length > 0 && (
        <>
          <Spacer size={20} />
          <Card>
            {addedBreads.map((bread) => {
              if (!bread.bread_ref) return
              return (
                <CampaignBreadCard
                  key={bread.bread_ref.id}
                  breadRef={bread.bread_ref}
                  breadName={getBreadShortName(bread)}
                  handleBreadIds={handleBreadIds}
                  isAdded={true}
                  isTypeDaily={bread.is_type_daily}
                  isTypePreorder={bread.is_type_preorder}
                  isTypeDelivery={!!bread.is_type_delivery}
                  imageUrl={bread.image_urls[0] || ''}
                />
              )
            })}
          </Card>
        </>
      )}
      <Spacer size={20} />
      <Background color={'grey'}>
        <SearchForm
          isFilled={keyword !== ''}
          onChange={handleKeyword}
          onClick={searchBreads}
          placeholder={'商品名か店舗名で検索'}
          value={keyword}
        />
        <Spacer size={12} />
        <FilterLabel label={'注文タイプ：'} />
        <Spacer size={8} />
        <FlexRow justifyContent={'start'} wrap={'wrap'}>
          {BREAD_TYPES.map((BREAD_TYPE) => {
            const { id, name } = BREAD_TYPE
            return (
              <TagCheckbox
                key={id}
                hasHashtag={false}
                id={id}
                isChecked={!isAllBreadTypesSelected && breadTypes.some((type) => type === id)}
                onChange={handleBreadTypes}
                type={'outlined'}
                value={name}
              />
            )
          })}
          <TagCheckbox
            hasHashtag={false}
            id={'all'}
            isChecked={isAllBreadTypesSelected}
            onChange={selectAllBreadTypes}
            type={'outlined'}
            value={'すべて'}
          />
        </FlexRow>
        {searchedBreads.length > 0 && (
          <>
            <Spacer size={20} />
            <Card>
              {searchedBreads.map((bread) => {
                if (!bread.bread_ref) return
                // 追加済の商品は除外
                if (breadIds.includes(bread.bread_ref.id)) return
                // 選択中の注文タイプに応じて表示
                if (
                  // 「すべて」が選択されている
                  (breadTypes.includes('daily') &&
                    breadTypes.includes('preorder') &&
                    breadTypes.includes('delivery')) ||
                  // 特定の種別が選択されている
                  breadTypes.some((type) => {
                    return (
                      (type === 'daily' && bread.is_type_daily) ||
                      (type === 'preorder' && bread.is_type_preorder) ||
                      (type === 'delivery' && bread.is_type_delivery)
                    )
                  })
                ) {
                  return (
                    <CampaignBreadCard
                      key={bread.bread_ref.id}
                      breadRef={bread.bread_ref}
                      breadName={getBreadShortName(bread)}
                      handleBreadIds={handleBreadIds}
                      isAdded={false}
                      isTypeDaily={bread.is_type_daily}
                      isTypePreorder={bread.is_type_preorder}
                      isTypeDelivery={!!bread.is_type_delivery}
                      imageUrl={bread.image_urls[0] || ''}
                    />
                  )
                } else {
                  return
                }
              })}
            </Card>
          </>
        )}
      </Background>
      <Spacer size={48} />
      <BlueButton
        disabled={
          name === '' ||
          banner === '' ||
          mainVisual === '' ||
          background === '' ||
          periodStart === '' ||
          periodEnd === '' ||
          status === '' ||
          (status === 'public' && breadIds.length === 0)
        }
        endIconPath={'/icon/white/arrow_next_small.svg'}
        label={'確認画面へ'}
        onClick={goToConfirmPage}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/admin/shops'} />
      <Spacer size={72} />
    </div>
  )
}

export default CampaignEditTemplate
